/**
 * Offline (E12.3): Warteschlange in IndexedDB – Artikel mit Foto (als Blob, keine Größengrenze wie bei localStorage) und
 * Preisänderungen. Gesendet wird, sobald der Browser wieder online ist oder die Person es antippt. Fällt IndexedDB aus
 * (privater Modus), bleibt alles im Speicher der Sitzung.
 */
import { useEffect, useState } from "react";
import type { ArtikelAnlegen, PreisSenken } from "@shared/schemas/artikel";

const DB = "rl-offline";
const STORE = "warteschlange";
const MAX = 100;

export type Wartend =
  | { id: string; art: "artikel"; daten: ArtikelAnlegen; foto: Blob | null; erstelltAt: string }
  | { id: string; art: "preis"; daten: PreisSenken; foto: null; erstelltAt: string };

const listener = new Set<() => void>();
function melde() {
  for (const f of listener) f();
}

let notfall: Wartend[] = [];

function oeffnen(): Promise<IDBDatabase | null> {
  return new Promise((ok) => {
    try {
      if (!("indexedDB" in window)) return ok(null);
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "id" });
      req.onsuccess = () => ok(req.result);
      req.onerror = () => ok(null);
    } catch {
      ok(null);
    }
  });
}

async function alle(): Promise<Wartend[]> {
  const db = await oeffnen();
  if (!db) return notfall;
  return new Promise((ok) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => ok((req.result as Wartend[]).sort((a, b) => a.erstelltAt.localeCompare(b.erstelltAt)));
    req.onerror = () => ok(notfall);
  });
}

async function schreiben(w: Wartend): Promise<void> {
  const db = await oeffnen();
  if (!db) {
    notfall.push(w);
    melde();
    return;
  }
  await new Promise<void>((ok) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(w);
    tx.oncomplete = () => ok();
    tx.onerror = () => ok();
  });
  melde();
}

async function entfernen(id: string): Promise<void> {
  const db = await oeffnen();
  if (!db) {
    notfall = notfall.filter((x) => x.id !== id);
    melde();
    return;
  }
  await new Promise<void>((ok) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => ok();
    tx.onerror = () => ok();
  });
  melde();
}

export async function warteschlange(): Promise<Wartend[]> {
  return alle();
}

export async function offlineMerken(daten: ArtikelAnlegen, foto: Blob | null): Promise<boolean> {
  if ((await alle()).length >= MAX) return false;
  await schreiben({ id: crypto.randomUUID(), art: "artikel", daten, foto, erstelltAt: new Date().toISOString() });
  return true;
}

export async function offlinePreisMerken(daten: PreisSenken): Promise<boolean> {
  if ((await alle()).length >= MAX) return false;
  await schreiben({ id: crypto.randomUUID(), art: "preis", daten, foto: null, erstelltAt: new Date().toISOString() });
  return true;
}

/** Netzfehler erkennen: fetch wirft TypeError, oder der Browser meldet offline. */
export function istNetzfehler(e: unknown): boolean {
  return !navigator.onLine || (e instanceof TypeError && /fetch|network|load failed/i.test(e.message));
}

let laeuft = false;
/** Alle wartenden Einträge senden; liefert, wie viele erfolgreich waren. */
export async function offlineSenden(): Promise<number> {
  if (laeuft || !navigator.onLine) return 0;
  laeuft = true;
  let gesendet = 0;
  try {
    for (const w of await alle()) {
      const res =
        w.art === "artikel"
          ? await fetch("/api/mieter/artikel", { method: "POST", headers: { "content-type": "application/json" }, credentials: "same-origin", body: JSON.stringify(w.daten) })
          : await fetch("/api/mieter/artikel/preis-senken", { method: "POST", headers: { "content-type": "application/json" }, credentials: "same-origin", body: JSON.stringify(w.daten) });
      if (res.status === 401) break; // nicht angemeldet – später
      if (!res.ok && res.status !== 400 && res.status !== 409 && res.status !== 404) break; // Server/Netz – später noch einmal
      if (res.ok && w.art === "artikel" && w.foto) {
        try {
          const { artikel } = (await res.json()) as { artikel: { id: string }[] };
          for (const a of artikel) await fetch(`/api/mieter/artikel/${a.id}/foto`, { method: "PUT", body: w.foto, headers: { "content-type": w.foto.type || "image/jpeg" }, credentials: "same-origin" });
        } catch {
          // Foto fehlt dann – Artikel ist angelegt
        }
      }
      // 400/404/409: ungültig oder überholt → aus der Schlange, sonst hängt sie für immer
      await entfernen(w.id);
      if (res.ok) gesendet++;
    }
  } finally {
    laeuft = false;
  }
  return gesendet;
}

/** Zahl der wartenden Einträge, reagiert auf Änderungen und auf „online“. */
export function useOfflineWarteschlange(): { anzahl: number; senden: () => Promise<number> } {
  const [anzahl, setAnzahl] = useState(0);
  useEffect(() => {
    const f = () => void alle().then((l) => setAnzahl(l.length));
    listener.add(f);
    f();
    const online = () => void offlineSenden().then(f);
    window.addEventListener("online", online);
    if (navigator.onLine) void alle().then((l) => l.length && online());
    return () => {
      listener.delete(f);
      window.removeEventListener("online", online);
    };
  }, []);
  return { anzahl, senden: offlineSenden };
}
